import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Paper,
  Stack,
  Typography,
  Button,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import { useNavigate } from "react-router-dom";
import { getLangFromCookie, LANG_COOKIE } from "../../Utils/LangCookie";

/* ---------------- i18n ---------------- */

const I18N = {
  title: {
    ZH: "台灣水禾感謝您的申請!!",
    EN: "Taiwan SEHO thanks you for your application!!",
    JP: "台湾SEHOは、あなたの応募に感謝します。",
  },
  line: {
    ZH: [
      "我們已經收到您的合作資料,感謝您對台灣水禾的信任與支持",
      "我們將由專業人員盡快確認您的需求,並於1-3個工作天內與您聯繫,提供最適合的合作方案與產品資訊",
      "在聯繫之前,您也可以先瀏覽我們的品牌理念, ",
      "產品特色與成功合作案例,進一步了解台灣水禾,如何為不同產業打造更安心 更有效率的清潔方案",
      "再次感謝您的來信期待與您攜手,讓潔淨更安全,更永續。",
    ],
    EN: [
      "We have received your cooperation information. Thank you for your trust and support for Taiwan SEHO.",
      "Our professional team will review your needs as soon as possible and contact you within 1–3 business days to provide the most suitable cooperation plan and product information.",
      "Before we contact you, you may also browse our brand philosophy, product features, and successful cooperation cases to learn more about how Taiwan SEHO creates safer and more efficient cleaning solutions for different industries.",
      "Thank you again for your message. We look forward to working with you to make cleanliness safer and more sustainable.",
    ],
    JP: [
      "協業に関する資料を受け付けました。台湾SEHOへの信頼とご支持に感謝いたします。",
      "専門スタッフができるだけ早くご要望を確認し、1〜3営業日以内にご連絡のうえ、最適な協業プランと製品情報をご案内いたします。",
      "ご連絡までの間、ブランド理念、製品の特長、成功した協業事例もぜひご覧いただき、台湾SEHOがさまざまな業界に向けて、より安心で効率的な清潔ソリューションをどのように提供しているかをご確認ください。",
      "改めてお問い合わせいただきありがとうございます。清潔をより安全に、より持続可能にするため、皆さまとご一緒できることを楽しみにしております。",
    ],
  },
  reference: {
    ZH: "申請編號",
    EN: "Reference ID",
    JP: "受付番号",
  },
  actions: {
    primary: {
      ZH: "前往",
      EN: "Go",
      JP: "移動する",
    },
    secondary: {
      ZH: "返回",
      EN: "Back",
      JP: "戻る",
    },
  },
};

function normalizeLang(lang) {
  const s = String(lang || "")
    .toUpperCase()
    .trim();
  return s === "EN" || s === "JP" || s === "ZH" ? s : "ZH";
}

/**
 * SuccessSubmitDialog
 *
 * Props:
 * - open: boolean
 * - onClose?: () => void
 *
 * - primaryHref?: string        // where primary button navigates
 * - primaryLabel?: {ZH,EN,JP}   // optional label override
 *
 * - secondaryHref?: string       // where secondary button navigates
 * - secondaryLabel?: {ZH,EN,JP}  // optional label override
 * - secondaryCloseOnly?: boolean // close without navigating
 *
 * - referenceValue?: string      // optional submitted reference ID
 * - referenceLabel?: {ZH,EN,JP}  // optional reference-label override
 *
 * - disableBackdropClose?: boolean // default true
 */
export default function SuccessSubmitDialog({
  open,
  onClose,

  primaryHref = "/",
  primaryLabel,

  secondaryHref = "",
  secondaryLabel,
  secondaryCloseOnly = false,

  referenceValue = "",
  referenceLabel,

  disableBackdropClose = true,
}) {
  const nav = useNavigate();
  const [lang, setLang] = useState(() =>
    normalizeLang(getLangFromCookie() || "ZH"),
  );

  useEffect(() => {
    const syncLang = () => {
      const c = normalizeLang(
        getLangFromCookie() || localStorage.getItem(LANG_COOKIE) || "ZH",
      );
      setLang(c);
    };
    syncLang();
    window.addEventListener("app:languageChanged", syncLang);
    return () => window.removeEventListener("app:languageChanged", syncLang);
  }, []);

  const font = {
    ZH: "LINE Seed TW",
    EN: "LINE Seed Sans",
    JP: "LINE Seed JP",
  };

  const primaryText = primaryLabel?.[lang] || I18N.actions.primary[lang];

  const secondaryText =
    knowing(secondaryLabel, lang) || I18N.actions.secondary[lang];

  const referenceText = knowing(referenceLabel, lang) || I18N.reference[lang];

  const normalizedReferenceValue = String(referenceValue || "").trim();

  function knowing(lbl, L) {
    if (!lbl) return "";
    return lbl[L] || lbl.ZH || "";
  }

  const handlePrimary = () => {
    if (onClose) onClose();
    nav(primaryHref);
  };

  const handleSecondary = () => {
    if (onClose) {
      onClose();
    }

    if (secondaryCloseOnly) {
      return;
    }

    if (secondaryHref) {
      nav(secondaryHref);
    } else {
      nav(-1);
    }
  };

  const handleDialogClose = (_e, reason) => {
    if (
      disableBackdropClose &&
      (reason === "backdropClick" || reason === "escapeKeyDown")
    )
      return;
    if (onClose) onClose();
  };

  return (
    <Dialog
      open={!!open}
      onClose={handleDialogClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "transparent",
          boxShadow: "none",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            p: { xs: 3, md: 5 },
            position: "relative",
            overflow: "hidden",
            bgcolor: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(2px)",
          }}
        >
          <Stack spacing={2.2} alignItems="center">
            <Typography
              sx={{
                fontFamily: font[lang],
                fontWeight: 700,
                fontSize: { xs: 22, md: 32 },
                textAlign: "center",
              }}
            >
              {I18N.title[lang]}
            </Typography>

            <VerifiedIcon
              sx={{ color: "#22c55e", fontSize: { xs: 48, md: 66 } }}
            />

            <Stack spacing={1.1} sx={{ width: "100%" }}>
              {(I18N.line[lang] || I18N.line.ZH).map((lineText, lineIndex) => (
                <Typography
                  key={`success-line-${lineIndex}`}
                  sx={{
                    fontFamily: font[lang],
                    textAlign: "center",
                    fontSize: { xs: 12, md: 18 },
                    lineHeight: { xs: 1.65, md: 1.75 },
                  }}
                >
                  {lineText}
                </Typography>
              ))}
            </Stack>

            {!!normalizedReferenceValue && (
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 480,
                  px: { xs: 2, sm: 3 },
                  py: 2,
                  borderRadius: 2,
                  bgcolor: "rgba(34,197,94,0.08)",
                  border: "1px solid rgba(34,197,94,0.28)",
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: font[lang],
                    fontSize: {
                      xs: 12,
                      md: 14,
                    },
                    fontWeight: 700,
                    color: "text.secondary",
                  }}
                >
                  {referenceText}
                </Typography>

                <Typography
                  sx={{
                    mt: 0.5,
                    fontFamily: font[lang],
                    fontSize: {
                      xs: 15,
                      md: 18,
                    },
                    fontWeight: 700,
                    color: "text.primary",
                    overflowWrap: "anywhere",
                  }}
                >
                  {normalizedReferenceValue}
                </Typography>
              </Box>
            )}

            <DialogActions
              sx={{
                px: 0,
                pt: 2.2,
                width: "100%",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  gap: 1.25,
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "center",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleSecondary}
                  sx={{
                    fontFamily: font[lang],
                    fontSize: { xs: 12, md: 18, lg: 20 },
                    minWidth: { xs: "100%", sm: 220 },
                  }}
                >
                  {secondaryText}
                </Button>

                <Button
                  variant="contained"
                  onClick={handlePrimary}
                  sx={{
                    fontFamily: font[lang],
                    fontSize: { xs: 12, md: 18, lg: 20 },
                    minWidth: { xs: "100%", sm: 220 },
                  }}
                >
                  {primaryText}
                </Button>
              </Box>
            </DialogActions>
          </Stack>
        </Paper>
      </DialogContent>
    </Dialog>
  );
}
